// Maps a NatureServe `informalTaxonomy` (level1/level2/level3) to a header icon + color bucket for
// the species slide-over; the full domain tree lives at GET https://explorer.natureserve.org/api/data/informalTaxonomy.

// No theme tokens exist for taxonomy accents — these are meaningful constants.
const THEMES = {
  green: { gradient: 'linear-gradient(135deg, #16281D 0%, #234D36 65%, #2C5E40 100%)', accent: '#A5D6A7' },
  slateBlue: { gradient: 'linear-gradient(135deg, #17222E 0%, #274155 65%, #315470 100%)', accent: '#9EC7E8' },
  teal: { gradient: 'linear-gradient(135deg, #102A2E 0%, #1C4A4F 65%, #256066 100%)', accent: '#8FD3D6' },
  earthBrown: { gradient: 'linear-gradient(135deg, #241A12 0%, #43301F 65%, #55402C 100%)', accent: '#D8B48C' },
  wetlandAqua: { gradient: 'linear-gradient(135deg, #122726 0%, #1E4A46 65%, #276057 100%)', accent: '#93D6C4' },
  olive: { gradient: 'linear-gradient(135deg, #20240F 0%, #3C441C 65%, #4E5726 100%)', accent: '#CBD68C' },
  amber: { gradient: 'linear-gradient(135deg, #2A2410 0%, #4A3F1A 65%, #5E5022 100%)', accent: '#E6CE86' },
  plum: { gradient: 'linear-gradient(135deg, #251826 0%, #452F47 65%, #573959 100%)', accent: '#D6A9D8' },
  black: { gradient: 'linear-gradient(135deg, #000000 0%, #0A0A0A 65%, #141414 100%)', accent: '#BDBDBD' }
}

const DEFAULT = { icon: 'mdi-dna', ...THEMES.black }

// Ordered rules — first match wins, so specific level3 buckets precede the broad level2/level1
// fallbacks (e.g. "Butterflies" before the generic insect rule).
const RULES = [
  { key: 'bird', icon: 'mdi-bird', theme: 'slateBlue', test: t => t.level3 === 'Birds' },
  { key: 'mammal', icon: 'mdi-paw', theme: 'earthBrown', test: t => t.level3 === 'Mammals' },
  { key: 'fish', icon: 'mdi-fish', theme: 'teal', test: t => /^Fishes/.test(t.level3 || '') },
  { key: 'amphibian', icon: 'mdi-waves', theme: 'wetlandAqua', test: t => t.level3 === 'Amphibians' },
  { key: 'turtle', icon: 'mdi-turtle', theme: 'olive', test: t => t.level3 === 'Turtles' },
  { key: 'reptile', icon: 'mdi-snake', theme: 'olive', test: t => t.level3 === 'Reptiles' || t.level3 === 'Crocodilians' },
  { key: 'lepidoptera', icon: 'mdi-butterfly', theme: 'amber', test: t => t.level3 === 'Butterflies' || /Moths/.test(t.level3 || '') },
  { key: 'bee', icon: 'mdi-bee', theme: 'amber', test: t => /Bees/.test(t.level3 || '') || /Ants, Wasps/.test(t.level3 || '') },
  { key: 'arachnid', icon: 'mdi-spider', theme: 'amber', test: t => /Spiders/.test(t.level3 || '') },
  { key: 'mollusc', icon: 'mdi-snail', theme: 'plum', test: t => /Mussels|Snails|Molluscs/.test(t.level3 || '') },
  { key: 'conifer', icon: 'mdi-pine-tree', theme: 'green', test: t => t.level3 === 'Conifers' },
  { key: 'insect', icon: 'mdi-ladybug', theme: 'amber', test: t => /^Insects/.test(t.level2 || '') },
  { key: 'crustacean', icon: 'mdi-bug', theme: 'plum', test: t => /Crustaceans/.test(t.level2 || '') },
  { key: 'marineInvert', icon: 'mdi-jellyfish', theme: 'plum', test: t => t.level2 === 'Invertebrates - Marine' },
  { key: 'fungus', icon: 'mdi-mushroom', theme: 'green', test: t => t.level1 === 'Fungi' },
  // Whole-kingdom fallback — conifers are caught by the level3 rule above, so they keep their glyph
  { key: 'plant', icon: 'mdi-leaf', theme: 'green', test: t => t.level1 === 'Plants' || /Algae/.test(t.level1 || '') }
]

export function taxonomyPresentation (informalTaxonomy) {
  if (!informalTaxonomy || typeof informalTaxonomy !== 'object') return DEFAULT
  const rule = RULES.find(r => r.test(informalTaxonomy))
  if (!rule) return DEFAULT
  return { icon: rule.icon, ...THEMES[rule.theme] }
}
